import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@advanza/shadcn-ui/src/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@advanza/shadcn-ui/src/components/ui/table'
import { Button, Icon, LoadingDots } from '@advanza/ui'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { ArrowRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    addMapping,
    fetchCanonicalServiceOptions,
    fetchMappings,
    removeMapping,
    resetState,
    saveMappings,
} from './languageServiceMappingsSlice'
import { OptionItem } from './types'

interface OptionsMappingEditorProps {
    serviceId: number
    canonicalServiceId: number
    currentServiceQuestionIds: number[] | null
}

interface StoreState {
    services: {
        entities: {
            services?: Record<number, { name?: string }>
            questions?: Record<number, { options?: number[] }>
            options?: Record<number, OptionItem>
        }
    }
}

const OptionsMappingEditor = ({
    serviceId,
    canonicalServiceId,
    currentServiceQuestionIds,
}: OptionsMappingEditorProps) => {
    const dispatch = useAppDispatch()
    const { mappings, canonicalOptions, canonicalServiceName, isLoading, isSaving } =
        useAppSelector((state) => state.languageServiceMappings)

    const [newCanonicalOptionId, setNewCanonicalOptionId] = useState('')
    const [newLocalizedOptionId, setNewLocalizedOptionId] = useState('')

    const storeServices = useSelector((state: StoreState) => state.services.entities.services || {})
    const storeQuestions = useSelector(
        (state: StoreState) => state.services.entities.questions || {},
    )
    const storeOptions = useSelector((state: StoreState) => state.services.entities.options || {})

    const canonicalLabel = canonicalServiceName ?? `Service ${canonicalServiceId}`
    const localizedLabel = storeServices[serviceId]?.name ?? `Service ${serviceId}`

    // Build current service options from normalized Redux store
    const currentOptions: OptionItem[] = []
    if (currentServiceQuestionIds) {
        currentServiceQuestionIds.forEach((qId) => {
            const question = storeQuestions[qId]
            question?.options?.forEach((optId) => {
                const option = storeOptions[optId]
                if (option) {
                    currentOptions.push(option)
                }
            })
        })
    }

    useEffect(() => {
        dispatch(fetchMappings(serviceId))
        dispatch(fetchCanonicalServiceOptions(canonicalServiceId))
        return () => {
            dispatch(resetState())
        }
    }, [dispatch, serviceId, canonicalServiceId])

    const handleAdd = () => {
        if (!newCanonicalOptionId || !newLocalizedOptionId) {
            return
        }
        const newMapping = {
            canonical_option_id: parseInt(newCanonicalOptionId, 10),
            localized_option_id: parseInt(newLocalizedOptionId, 10),
        }
        const exists = mappings.some(
            (m) => m.canonical_option_id === newMapping.canonical_option_id,
        )
        if (exists) {
            return
        }
        dispatch(addMapping(newMapping))
        dispatch(saveMappings({ serviceId, mappings: [...mappings, newMapping] }))
        setNewCanonicalOptionId('')
        setNewLocalizedOptionId('')
    }

    const handleRemove = (canonicalOptionId: number) => {
        const updated = mappings.filter((m) => m.canonical_option_id !== canonicalOptionId)
        dispatch(removeMapping(canonicalOptionId))
        dispatch(saveMappings({ serviceId, mappings: updated }))
    }

    const getOptionLabel = (option: OptionItem | undefined) =>
        option ? `${option.value || option.option_id} (${option.option_id})` : 'Unknown'

    const findCanonicalOption = (optionId: number) =>
        canonicalOptions.find((o) => o.option_id === optionId)
    const findLocalizedOption = (optionId: number) =>
        currentOptions.find((o) => o.option_id === optionId)

    if (isLoading) {
        return <LoadingDots />
    }

    return (
        <div className="space-y-4">
            {mappings.length > 0 && (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{canonicalLabel}</TableHead>
                            <TableHead className="w-8" />
                            <TableHead>{localizedLabel}</TableHead>
                            <TableHead className="w-10" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {mappings.map((mapping) => (
                            <TableRow key={mapping.canonical_option_id}>
                                <TableCell>
                                    {getOptionLabel(
                                        mapping.canonical_option ??
                                            findCanonicalOption(mapping.canonical_option_id),
                                    )}
                                </TableCell>
                                <TableCell>
                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                </TableCell>
                                <TableCell>
                                    {getOptionLabel(
                                        mapping.localized_option ??
                                            findLocalizedOption(mapping.localized_option_id),
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        appearance="text_primary"
                                        disabled={isSaving}
                                        onClick={() => handleRemove(mapping.canonical_option_id)}>
                                        <Icon name="delete" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <div className="flex items-center gap-2">
                <Select value={newCanonicalOptionId} onValueChange={setNewCanonicalOptionId}>
                    <SelectTrigger className="w-full border border-neutral-200 bg-white">
                        <SelectValue placeholder={`${canonicalLabel} option...`} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                        {canonicalOptions.map((opt) => (
                            <SelectItem key={opt.option_id} value={String(opt.option_id)}>
                                {getOptionLabel(opt)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Select value={newLocalizedOptionId} onValueChange={setNewLocalizedOptionId}>
                    <SelectTrigger className="w-full border border-neutral-200 bg-white">
                        <SelectValue placeholder={`${localizedLabel} option...`} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                        {currentOptions.map((opt) => (
                            <SelectItem key={opt.option_id} value={String(opt.option_id)}>
                                {getOptionLabel(opt)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button
                    appearance="text_primary"
                    onClick={handleAdd}
                    disabled={!newCanonicalOptionId || !newLocalizedOptionId || isSaving}>
                    <Icon name="save" />
                </Button>
            </div>
        </div>
    )
}

export default OptionsMappingEditor
